/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 */

import axios, { AxiosInstance } from 'axios';
import type {
  Tenant,
  Car,
  CarSearchRequest,
  DomainStatus,
  OwnerSiteState,
  AuthUser,
  OrderBookingRequest,
  BookingResult,
  BookingInfo,
  ModelResolveRequest,
  ModelCompany,
  PluginBookingResult,
  SavedCard,
  CardOwner,
} from '../types';

const API_BASE_URL = '/api';
const TOKEN_KEY = 'authToken';
const USER_KEY = 'authUser';

/** Unwrap the backend's { reason, result } envelope (camel or Pascal cased). */
function unwrap<T>(data: any): T {
  if (data && typeof data === 'object' && ('result' in data || 'Result' in data)) {
    return (data.result ?? data.Result) as T;
  }
  return data as T;
}

const reasonOf = (data: any): number => Number(data?.reason ?? data?.Reason ?? 0);
const messageOf = (data: any): string | undefined => data?.message ?? data?.Message;

/** Which rental company's card set a card call addresses. Cards are kept per (driver, owner): pass the car being
 *  booked (the server takes its owner) and/or the owner id. Both optional — omitted, the server picks the
 *  storefront's owner or the driver's only company. */
export interface CardScope {
  ownerId?: string;
  carId?: string;
}

/** "?ownerId=…&carId=…" for a card scope ('' when empty). */
const scopeQuery = (scope?: CardScope): string => {
  const qs: string[] = [];
  if (scope?.ownerId) qs.push(`ownerId=${encodeURIComponent(scope.ownerId)}`);
  if (scope?.carId) qs.push(`carId=${encodeURIComponent(scope.carId)}`);
  return qs.length ? `?${qs.join('&')}` : '';
};

/** "pm~userId~ownerId" card reference (owner omitted → the older "pm~userId" form). */
const cardRef = (paymentMethodId: string, userId: string, ownerId?: string): string =>
  ownerId ? `${paymentMethodId}~${userId}~${ownerId}` : `${paymentMethodId}~${userId}`;

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      // Forward the real hostname so the backend resolves the tenant from the subdomain/domain,
      // even in dev where the SPA runs on a subdomain but the API is on another port.
      if (typeof window !== 'undefined') {
        const host =
          window.location.hostname +
          (window.location.port ? `:${window.location.port}` : '');
        config.headers['x-forwarded-host'] = host;
        config.headers['x-original-host'] = host;

        const token = localStorage.getItem('authToken');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });
  }

  // -------- storefront (public) --------

  /** Resolve the current tenant from the host; throws 404 on the main domain. */
  async getTenant(): Promise<Tenant> {
    const r = await this.client.get('/storefront/tenant');
    return unwrap<Tenant>(r.data);
  }

  async search(request: CarSearchRequest): Promise<Car[]> {
    const r = await this.client.post('/storefront/search', request);
    return unwrap<Car[]>(r.data) ?? [];
  }

  /** Distinct cities of owners with published cars (tenant-scoped on a storefront host). */
  async getCities(): Promise<string[]> {
    const r = await this.client.get('/storefront/cities');
    return unwrap<string[]>(r.data) ?? [];
  }

  /** Shared pickup locations served by this storefront (canonical, cross-owner). For the Location dropdown. */
  async getLocations(): Promise<import('../types').StorefrontLocation[]> {
    const r = await this.client.get('/storefront/locations');
    return unwrap<import('../types').StorefrontLocation[]>(r.data) ?? [];
  }

  /** Smart location search: map free text (city, landmark, airport "JFK"/"LAX", borough) to a served
   *  city via Claude Haiku. Returns the canonical city, or null when nothing matches. */
  async resolveLocation(query: string): Promise<string | null> {
    try {
      const r = await this.client.post('/storefront/resolve-location', { query });
      const res = unwrap<{ city: string | null }>(r.data);
      return res?.city ?? null;
    } catch {
      return null;
    }
  }

  /** Tenant office locations that have public contact details (email or phone), for the Contact page. */
  async getContactLocations(): Promise<import('../types').StorefrontContactLocation[]> {
    try {
      const r = await this.client.get('/storefront/contact-locations');
      return unwrap<import('../types').StorefrontContactLocation[]>(r.data) ?? [];
    } catch {
      return [];
    }
  }

  /** Public partner directory: active storefronts as { name, host }. */
  async getPartners(): Promise<{ name: string; host: string }[]> {
    const r = await this.client.get('/storefront/partners');
    return unwrap<{ name: string; host: string }[]>(r.data) ?? [];
  }

  async getCar(carId: string): Promise<Car | null> {
    const r = await this.client.get(`/storefront/car/${carId}`);
    const list = unwrap<Car[]>(r.data) ?? [];
    return list[0] ?? null;
  }

  // -------- owner-site management (authenticated tenant admin) --------

  async getOwnerSite(ownerId?: string): Promise<OwnerSiteState> {
    const r = await this.client.get('/owner-site', {
      params: ownerId ? { ownerId } : undefined,
    });
    return r.data as OwnerSiteState;
  }

  async saveOwnerSite(body: Partial<Tenant>, ownerId?: string): Promise<OwnerSiteState> {
    const r = await this.client.put('/owner-site', body, {
      params: ownerId ? { ownerId } : undefined,
    });
    return r.data as OwnerSiteState;
  }

  async claimSubdomain(subdomain: string, ownerId?: string) {
    const r = await this.client.post(
      '/owner-site/subdomain',
      { subdomain },
      { params: ownerId ? { ownerId } : undefined },
    );
    return r.data as { result: Tenant; fullDomain: string; status: string };
  }

  async addCustomDomain(domain: string, ownerId?: string) {
    const r = await this.client.post(
      '/owner-site/custom-domain',
      { domain },
      { params: ownerId ? { ownerId } : undefined },
    );
    return r.data as { result: Tenant; status: string; instructions: any };
  }

  async customDomainStatus(ownerId?: string) {
    const r = await this.client.get('/owner-site/custom-domain/status', {
      params: ownerId ? { ownerId } : undefined,
    });
    return r.data as { configured: boolean; domain?: string; status?: DomainStatus };
  }

  async removeCustomDomain(ownerId?: string) {
    const r = await this.client.delete('/owner-site/custom-domain', {
      params: ownerId ? { ownerId } : undefined,
    });
    return r.data;
  }

  // -------- auth (renter) --------

  getStoredUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  }

  /** True if the email is FREE (no account yet) → new-driver wizard; false → existing → ask password. */
  async isEmailFree(email: string): Promise<boolean> {
    const r = await this.client.post('/Users/emails/free', email.trim().toLowerCase());
    // result: true = available/new, false = taken/existing
    const res = unwrap<boolean>(r.data);
    return res !== false;
  }

  /** Read a driver-license image: front via OCR (analyze) or back via PDF417 barcode (parse). */
  async readLicense(file: File, side: 'front' | 'back'): Promise<Record<string, any> | null> {
    const form = new FormData();
    form.append('image', file);
    const url = side === 'back' ? '/license/parse-license-barcode' : '/license/analyze-driver-license';
    try {
      const r = await this.client.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const body = r.data;
      const ok = body?.success ?? body?.Success;
      const data = body?.data ?? body?.Data;
      return ok && data ? data : null;
    } catch {
      return null;
    }
  }

  /** Create a NEW renter with driver license (multipart) and sign them in (stores token + user). */
  async createUserWithLicense(fields: Record<string, string>, frontFile?: File | null, backFile?: File | null): Promise<AuthUser> {
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => { if (v != null && v !== '') form.append(k, v); });
    form.append('Role', 'Renter');
    if (frontFile) form.append('FrontSideImage', frontFile);
    if (backFile) form.append('BackSideImage', backFile);

    let r;
    try {
      r = await this.client.post('/RegistrationLinkRequest/create-user-with-driver-license', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (e: any) {
      // 409 = the details belong to an account that already exists: sign in (80301) or enter the code
      // texted to that account's phone (80302 / 80303). Keep the reason so the modal can react.
      if (e?.response?.status === 409) {
        throw Object.assign(new Error(messageOf(e.response.data) || 'Could not create your account.'),
          { reason: reasonOf(e.response.data) });
      }
      throw e;
    }
    const reason = reasonOf(r.data);
    if (reason !== 0) throw new Error(messageOf(r.data) || 'Could not create your account.');
    const res = unwrap<any>(r.data);
    const token: string = res?.token ?? res?.Token;
    if (!token) throw new Error('No token returned.');
    const user: AuthUser = {
      id: res.id ?? res.Id,
      email: res.email ?? res.Email,
      firstName: res.firstName ?? res.FirstName,
      lastName: res.lastName ?? res.LastName,
      token,
      tokenExpired: res.expired ?? res.Expired,
    };
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  }

  /** Sign in an existing Aegis renter. Throws on bad credentials with a readable message. */
  async signIn(email: string, password: string): Promise<AuthUser> {
    const r = await this.client.post('/UserAuth/signin', { email, password });
    const reason = reasonOf(r.data);
    if (reason !== 0) {
      throw new Error(messageOf(r.data) || 'Sign in failed.');
    }
    const res = unwrap<any>(r.data);
    const token: string = res?.token ?? res?.Token;
    if (!token) throw new Error('No token returned.');
    const user: AuthUser = {
      id: res.id ?? res.Id,
      email: res.email ?? res.Email,
      firstName: res.firstName ?? res.FirstName,
      lastName: res.lastName ?? res.LastName,
      token,
      tokenExpired: res.tokenExpired ?? res.TokenExpired,
    };
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  }

  signOut() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }

  // -------- booking --------

  /** Create a real booking request (status PendingApproval). Surfaces handled backend failures. */
  async orderBooking(req: OrderBookingRequest): Promise<BookingResult> {
    try {
      const r = await this.client.post('/Booking/order', {
        carId: req.carId,
        dateFrom: req.dateFrom,
        dateTo: req.dateTo,
        rentalType: req.rentalType,
        desiredPrice: 0,
        baseOfChatId: '',
        renterId: null,
        carTypeId: '',
      });
      const reason = reasonOf(r.data);
      if (reason !== 0) {
        return { ok: false, reason, message: messageOf(r.data) || 'Booking could not be created.' };
      }
      const result = unwrap<any>(r.data);
      const bookingId = Array.isArray(result) ? result[0] : result?.bookingId;
      const chatId = Array.isArray(result) ? result[1] : result?.chatId;
      return { ok: true, bookingId, chatId };
    } catch (e: any) {
      const data = e?.response?.data;
      return {
        ok: false,
        reason: reasonOf(data) || -1,
        message: messageOf(data) || e?.message || 'Booking request failed.',
      };
    }
  }

  /** Booking config for a car: pricing, deposit, and whether the owner books through a plugin.
   *  Pass the rental start (and end) date (ISO) so the returned price reflects the active pricing season
   *  and any duration price-intervals. */
  async getBookingInfo(carId: string, dateFrom?: string, dateTo?: string): Promise<BookingInfo> {
    const qs: string[] = [];
    if (dateFrom) qs.push(`dateFrom=${encodeURIComponent(dateFrom)}`);
    if (dateTo) qs.push(`dateTo=${encodeURIComponent(dateTo)}`);
    const q = qs.length ? `?${qs.join('&')}` : '';
    const r = await this.client.get(`/storefront/booking-info/${carId}${q}`);
    return unwrap<BookingInfo>(r.data);
  }

  /** Resolve the first available car of a make/model + how many of that model are available. */
  async resolveModelCar(req: ModelResolveRequest): Promise<{ car: Car | null; availableCount: number }> {
    const r = await this.client.post('/storefront/book-model', req);
    const res = unwrap<{ car: Car | null; availableCount: number }>(r.data);
    return { car: res?.car ?? null, availableCount: res?.availableCount ?? 0 };
  }

  /** Companies offering a model for the dates (for the multi-company picker). */
  async getModelCompanies(req: ModelResolveRequest): Promise<ModelCompany[]> {
    try {
      const r = await this.client.post('/storefront/model-companies', req);
      return unwrap<ModelCompany[]>(r.data) ?? [];
    } catch {
      return [];
    }
  }

  /** Native full booking (create + approve + insurance + WaitingForAgreement). Returns the bookingId. */
  async createCompleteBooking(
    req: OrderBookingRequest & { carTypeId?: string; additionalServices?: { additionalServiceId: string; quantity: number }[] },
  ): Promise<BookingResult> {
    try {
      const r = await this.client.post('/Booking/create-complete-booking', {
        carId: req.carId,
        dateFrom: req.dateFrom,
        dateTo: req.dateTo,
        renterId: null,
        carTypeId: req.carTypeId ?? '',
        additionalServices: req.additionalServices ?? [],
      });
      const reason = reasonOf(r.data);
      if (reason !== 0) {
        return { ok: false, reason, message: messageOf(r.data) || 'Booking could not be created.' };
      }
      const res = unwrap<any>(r.data);
      return { ok: true, bookingId: res?.bookingId ?? res?.BookingId, message: res?.message ?? res?.Message };
    } catch (e: any) {
      const data = e?.response?.data;
      return { ok: false, reason: reasonOf(data) || -1, message: messageOf(data) || e?.message || 'Booking request failed.' };
    }
  }

  /** Create a booking THROUGH the tenant owner's booking plugin (e.g. HQ Rental). */
  async createPluginBooking(req: { carId: string; dateFrom: string; dateTo: string; additionalServices?: { additionalServiceId: string; quantity: number }[] }): Promise<PluginBookingResult> {
    try {
      const r = await this.client.post('/storefront/booking', { ...req, additionalServices: req.additionalServices ?? [] });
      const res = unwrap<PluginBookingResult>(r.data);
      return res ?? { ok: false, message: 'No response from booking platform.' };
    } catch (e: any) {
      const data = e?.response?.data;
      return { ok: false, message: messageOf(data) || e?.message || 'Booking request failed.' };
    }
  }

  /** Days to disable in the booking calendar: a car's booked days, or (for a model) the days on
   *  which every car of the model is booked. Returns a set of yyyy-MM-dd strings. */
  async getAvailabilityCalendar(req: {
    carId?: string; make?: string; model?: string; city?: string; ownerId?: string; from: string; to: string;
  }): Promise<Set<string>> {
    try {
      const r = await this.client.post('/storefront/availability-calendar', req);
      const res = unwrap<{ busyDays: string[] }>(r.data);
      return new Set(res?.busyDays ?? []);
    } catch {
      return new Set();
    }
  }

  /** Is a specific car free for the given window? Re-checked when the renter changes dates. */
  async checkAvailability(carId: string, dateFrom: string, dateTo: string): Promise<boolean> {
    try {
      const r = await this.client.post('/storefront/availability', { carId, dateFrom, dateTo });
      const res = unwrap<{ available: boolean }>(r.data);
      return !!res?.available;
    } catch {
      // Fail open: if availability can't be determined (e.g. endpoint not deployed yet), don't block —
      // the booking-creation path still rejects a true date overlap server-side.
      return true;
    }
  }

  /** Ready Stripe Checkout setup URL for the signed-in renter to add a card on file — into the card set of the
   *  car's owner (carId) or the given company (ownerId); the server falls back to the storefront's owner. */
  async getCardCheckoutUrl(scope?: CardScope): Promise<{ url: string | null; external: boolean }> {
    try {
      const r = await this.client.get(`/storefront/card-checkout-url${scopeQuery(scope)}`);
      const res = unwrap<{ url: string; external?: boolean }>(r.data);
      return { url: res?.url ?? null, external: !!res?.external };
    } catch {
      return { url: null, external: false };
    }
  }

  /** Rental-agreement data for a booking (renter, vehicle, dates, pricing, template URL). */
  async getAgreementData(bookingId: string): Promise<Record<string, any> | null> {
    try {
      const r = await this.client.get(`/Agreement/get-agreement-data/${bookingId}`);
      return unwrap<Record<string, any>>(r.data) ?? null;
    } catch {
      return null;
    }
  }

  /** Upload the signed rental-agreement PDF for a booking. Returns { ok, needsCard }. */
  async acceptAgreement(bookingId: string, pdf: Blob): Promise<{ ok: boolean; needsCard: boolean; message?: string }> {
    try {
      const form = new FormData();
      form.append('SignedPdfFile', pdf, `agreement-${bookingId}.pdf`);
      const r = await this.client.post(`/Agreement/rental-agreement/${bookingId}/accept`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const body = r.data?.result ?? r.data;
      const ok = body?.success ?? body?.Success ?? r.data?.reason === 0;
      return { ok: !!ok, needsCard: !!(body?.needsCard ?? body?.NeedsCard), message: body?.message ?? body?.Message };
    } catch (e: any) {
      return { ok: false, needsCard: false, message: e?.response?.data?.message ?? e?.message };
    }
  }

  /** The rental companies the signed-in driver keeps (or may start) a card set with — cards are per company. */
  async getMyCardOwners(): Promise<CardOwner[]> {
    const r = await this.client.get('/tollPayment/my-card-owners');
    const list = unwrap<any[]>(r.data) ?? [];
    return list
      .map((x) => ({ ownerId: x?.ownerId ?? x?.OwnerId ?? '', name: x?.name ?? x?.Name ?? '' }))
      .filter((o) => !!o.ownerId);
  }

  /** The renter's saved cards on file with one company (used to decide whether a card-capture step is needed).
   *  Without a scope the server picks the storefront's owner or the driver's only company. */
  async getCards(userId: string, scope?: CardScope): Promise<SavedCard[]> {
    try {
      const r = await this.client.get(`/tollPayment/get-cards-byId/${userId}${scopeQuery(scope)}`);
      return unwrap<SavedCard[]>(r.data) ?? [];
    } catch {
      return [];
    }
  }

  /** Make one of the renter's saved cards (in the given company's set) the active (default) charge card. */
  async setActiveCard(userId: string, paymentMethodId: string, scope?: CardScope): Promise<{ ok: boolean; message?: string }> {
    try {
      // [FromBody] string on the backend: send the JSON-encoded string. "pm~userId~ownerId" addresses the renter
      // by id (like get-cards-byId), so it works whether or not a JWT is attached; carId rides in the query.
      const r = await this.client.post(
        `/tollPayment/set-active-card-plain${scopeQuery(scope?.carId ? { carId: scope.carId } : undefined)}`,
        JSON.stringify(cardRef(paymentMethodId, userId, scope?.ownerId)),
      );
      return { ok: reasonOf(r.data) === 0 && unwrap<boolean>(r.data) !== false, message: messageOf(r.data) };
    } catch (e: any) {
      return { ok: false, message: e?.response?.data?.message ?? e?.message };
    }
  }

  /** Remove a saved card from the given company's set. The backend refuses to delete the active card while
   *  another card could take over — set another active first. */
  async deleteCard(userId: string, paymentMethodId: string, scope?: CardScope): Promise<{ ok: boolean; message?: string }> {
    try {
      const r = await this.client.post(
        '/tollPayment/delete-payment-method-card',
        JSON.stringify(cardRef(paymentMethodId, userId, scope?.ownerId)),
      );
      return { ok: reasonOf(r.data) === 0, message: messageOf(r.data) };
    } catch (e: any) {
      return { ok: false, message: e?.response?.data?.message ?? e?.message };
    }
  }

  // -------- my bookings (authenticated renter) --------

  /** All bookings of the signed-in renter (any status), newest first. */
  async getRenterBookings(): Promise<import('../types').RenterBooking[]> {
    const r = await this.client.get('/Booking/get-renter-bookings');
    const res = unwrap<any>(r.data);
    const list: any[] = res?.bookings ?? res?.Bookings ?? [];
    return list.map((x) => ({
      bookingId: x.bookingId ?? x.BookingId,
      carId: x.carId ?? x.CarId,
      carTitle: x.carTitle ?? x.CarTitle,
      carBrand: x.carBrand ?? x.CarBrand,
      carModel: x.carModel ?? x.CarModel,
      carYear: x.carYear ?? x.CarYear,
      carImage: x.carImage ?? x.CarImage,
      carPlateNumber: x.carPlateNumber ?? x.CarPlateNumber,
      dateFrom: x.dateFrom ?? x.DateFrom,
      dateTo: x.dateTo ?? x.DateTo,
      price: x.price ?? x.Price ?? 0,
      status: x.status ?? x.Status ?? 0,
      rentalType: x.rentalType ?? x.RentalType ?? 0,
      agreementPdfUrl: x.agreementPdfUrl ?? x.AgreementPdfUrl,
    }));
  }

  /** Ask the backend which kind of cancellation applies (Simple / Deposit / Payment). */
  async checkCancelBooking(bookingId: string): Promise<number> {
    const r = await this.client.post('/Booking/checkcancel', { bookingId });
    const res = unwrap<any>(r.data);
    // The backend may serialize the CancelType enum as a number or its name.
    const byName: Record<string, number> = { None: 0, Simple: 1, Deposit: 2, Payment: 3 };
    if (typeof res === 'string' && res in byName) return byName[res];
    const n = Number(res);
    return Number.isFinite(n) ? n : 1 /* Simple */;
  }

  /** Cancel a booking as the renter. Pass the cancelType from checkCancelBooking. */
  async cancelBooking(bookingId: string, cancelType: number): Promise<{ ok: boolean; message?: string }> {
    try {
      const r = await this.client.post('/Booking/cancel', {
        bookingId,
        cancelType,
        role: 0, // UserRole.Renter
      });
      const reason = reasonOf(r.data);
      if (reason !== 0) return { ok: false, message: messageOf(r.data) || 'Could not cancel the booking.' };
      return { ok: true };
    } catch (e: any) {
      return { ok: false, message: e?.response?.data?.message ?? e?.message ?? 'Could not cancel the booking.' };
    }
  }

  /** Change a booking's return date (DateTo). */
  async updateBookingDateTo(bookingId: string, dateTo: string): Promise<{ ok: boolean; message?: string }> {
    try {
      const r = await this.client.put('/Booking/update-booking-date-to', { bookingId, dateTo });
      const reason = reasonOf(r.data);
      if (reason !== 0) return { ok: false, message: messageOf(r.data) || 'Could not update the booking.' };
      return { ok: true };
    } catch (e: any) {
      return { ok: false, message: e?.response?.data?.message ?? e?.message ?? 'Could not update the booking.' };
    }
  }

  // -------- "scan your license with your phone" (QR + SSE push) --------

  /** Start a phone-capture session. Returns the session id to encode in the QR. */
  async createMobileLicenseSession(): Promise<string> {
    const r = await this.client.post('/license/mobile-session');
    return r.data?.sessionId ?? r.data?.SessionId;
  }

  /** SSE endpoint the desktop subscribes to (root-relative so it goes through the same proxy). */
  mobileLicenseEventsUrl(sessionId: string): string {
    return `${API_BASE_URL}/license/mobile-session/${sessionId}/events`;
  }

  /** Absolute URL the QR points the phone at. */
  mobileLicensePageUrl(sessionId: string): string {
    return `${window.location.origin}/m/license/${sessionId}`;
  }

  /** Phone uploads one side of the license. */
  async uploadMobileLicensePhoto(sessionId: string, side: 'front' | 'back', file: File): Promise<boolean> {
    const form = new FormData();
    form.append('side', side);
    form.append('image', file);
    try {
      const r = await this.client.post(`/license/mobile-session/${sessionId}/photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return (r.data?.success ?? r.data?.Success) === true;
    } catch {
      return false;
    }
  }

  /** Desktop pulls the original photo a phone uploaded, as a File to attach to the new driver. */
  async getMobileLicenseImage(sessionId: string, side: 'front' | 'back'): Promise<File | null> {
    try {
      const r = await this.client.get(`/license/mobile-session/${sessionId}/image/${side}`, { responseType: 'blob' });
      const blob: Blob = r.data;
      if (!blob || blob.size === 0) return null;
      const ext = blob.type.includes('png') ? 'png' : 'jpg';
      return new File([blob], `license-${side}.${ext}`, { type: blob.type || 'image/jpeg' });
    } catch {
      return null;
    }
  }
}

const api = new ApiService();
export default api;
